/**
 * Thrivia Embed SDK: self-location helpers shared by the loader entry and the legacy shim.
 *
 * Purpose: everything an embedded script needs in order to answer "where was I served from?"
 * without ever consulting `window.location`. This is the single most important rule in the whole
 * bundle. Our scripts run on somebody else's domain, so `window.location` is the ORGANIZER's
 * origin, not ours. The pre-existing `checkout-button.js` got this wrong (it loaded
 * `/embed/thrivia-button.css`, a root-relative path that resolves against the organizer's host and
 * 404s there, which is why every legacy button renders unstyled). Every absolute URL the bundles
 * build is derived from the script tag's own `src`.
 *
 * Used by: `index.ts` (loader entry) and `shim.ts` (legacy `checkout-button.js`). Both are
 * separate esbuild entry points, so each gets its own tree-shaken copy of only what it calls.
 *
 * No runtime dependencies: DOM only, ES2019 output target.
 */

/**
 * Origin used when self-location fails outright (no script element found, unparseable src).
 * Deliberately the production origin and NOT `window.location.origin`: on an organizer's page the
 * latter is guaranteed wrong, and a wrong-but-plausible origin produces silent 404s that look like
 * "the embed just does not work". Falling back to production at least keeps a real deployment
 * working when a tag manager strips the script's attributes.
 */
export const FALLBACK_ORIGIN = "https://www.thrivia.io";

/**
 * Matches the loader's own public path, with or without a query string or hash
 * (`/embed/v1.js`, `/embed/v1.js?v=2`). Used both to find our own tag when
 * `document.currentScript` is unavailable and to detect an already-present loader tag.
 */
export const LOADER_SRC_RE = /\/embed\/v1\.js(?:[?#]|$)/i;

/** Matches the legacy shim's public path. Same reasoning as LOADER_SRC_RE. */
export const SHIM_SRC_RE = /\/embed\/checkout-button\.js(?:[?#]|$)/i;

/**
 * Stamped by the loader onto every element it has bound a handler to.
 *
 * This is the handover token between the loader and the legacy shim, and it is deliberately
 * per-element rather than a global capability flag. The two find their targets by different
 * selectors: legacy snippets carry `data-thrivia-action="checkout"` with `data-thrivia-event-id`,
 * while new mounts carry `data-thrivia-event`. A shim that stood down on a global "button mode is
 * live" flag would abandon every legacy CTA the loader does not actually claim, silently turning a
 * new-tab open into a same-tab navigation on sites that have not changed a line of their HTML.
 * Checking for the stamp makes that failure structurally impossible: no stamp, no handover.
 */
/**
 * The attribute-name prefix, tokenised because it ships 18 times.
 *
 * A pure byte decision and it changes no contract: every attribute name below is byte-identical to
 * what an organizer pastes. Written as `DT + "name"` rather than as a full literal at each site
 * because esbuild minifies code, not the contents of string literals, so 18 copies of
 * "data-thrivia-" is 234 bytes on every organizer's page. See the size table in the Task 8 report.
 */
export const DT = "data-thrivia-";

export const BOUND_ATTR = DT + "bound";

/**
 * Finds a script element by its `src`, preferring the one currently executing.
 *
 * Two callers: a bundle locating ITSELF (pass its own path pattern), and the shim checking whether
 * a loader tag is already on the page (pass the loader pattern).
 *
 * `document.currentScript` is correct for classic scripts (including `async`/`defer`) and is tried
 * first. It is null when the code is evaluated rather than fetched, which happens for real in the
 * wild: Google Tag Manager custom HTML tags, Wix's sandbox shim, and any host that inlines our
 * bundle. The fallback walks the script tags backwards looking for our public path, newest first,
 * because when a page carries several copies the last one is the one that just ran.
 *
 * THE TRUST MODEL, stated because the fallback looks steerable and is: whoever can add a script tag
 * to the organizer's page can add one whose `src` matches LOADER_SRC_RE on a host they control, and
 * this function will then hand them the origin every URL in the bundle is built from. That is not a
 * gate worth adding, because the same attacker already runs script in the organizer's document -
 * they can read the DOM, replace our mount and draw their own checkout without involving us at all.
 * Restricting the match to thrivia.io hosts would buy nothing against them and would break the two
 * legitimate cases that do not run on that host: local development and preview deployments.
 */
export function findScript(pattern: RegExp): HTMLScriptElement | null {
  const current = document.currentScript as HTMLScriptElement | null;
  if (current && current.src && pattern.test(current.src)) return current;

  const scripts = document.getElementsByTagName("script");
  for (let i = scripts.length - 1; i >= 0; i--) {
    const candidate = scripts[i];
    if (candidate.src && pattern.test(candidate.src)) return candidate;
  }
  return null;
}

/**
 * Resolves an absolute origin (scheme + host + port) from a script `src`.
 *
 * `src` read off a live element is already absolute in every browser, but the value can also come
 * from `getAttribute` (relative) in the fallback paths, so it is resolved against `document.baseURI`
 * first. The anchor-element fallback covers engines where `URL` construction throws.
 */
export function resolveOrigin(src: string | null | undefined): string {
  if (!src) return FALLBACK_ORIGIN;
  try {
    return new URL(src, document.baseURI).origin;
  } catch {
    try {
      const anchor = document.createElement("a");
      anchor.href = src;
      // `a.origin` is missing on some older WebKit builds; protocol + host is universal.
      return anchor.protocol && anchor.host ? anchor.protocol + "//" + anchor.host : FALLBACK_ORIGIN;
    } catch {
      return FALLBACK_ORIGIN;
    }
  }
}

/** Resolves an absolute URL for `src`, falling back to the production origin. */
export function resolveUrl(src: string | null | undefined): string | null {
  if (!src) return null;
  try {
    return new URL(src, document.baseURI).href;
  } catch {
    return null;
  }
}

/**
 * Reads a boolean data attribute.
 *
 * Both HTML idioms count as true: the explicit `data-thrivia-preview="true"` that the spec's
 * snippets use, and the bare `data-thrivia-autolink` that organizers type by hand. Anything else,
 * including the literal "false", is false, so a generated snippet can carry `="false"` to turn a
 * flag off without deleting the attribute.
 */
export function readFlag(el: Element | null, name: string): boolean {
  if (!el) return false;
  const raw = el.getAttribute(name);
  if (raw === null) return false;
  return raw === "" || raw.toLowerCase() === "true";
}

/** Reads a trimmed string data attribute, returning null for missing or blank values. */
export function readAttr(el: Element | null, name: string): string | null {
  if (!el) return null;
  const raw = el.getAttribute(name);
  if (raw === null) return null;
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}
